import type { ReactNode } from "react";
import { SchemaDiagram } from "../../lib/funnel/SchemaDiagram";
import { DEMO_FUNNEL_TABLES, DEMO_FUNNEL_JOINS } from "./schemaConfig";

// Plain-language explanation blocks fed to the QueryInspector "Explain" tab.
// Rendered as a responsive card grid so the modal reads vertically rather
// than as a long single-column wall of paragraphs.

type Block = { title: string; body: ReactNode };

const DEMO_BLOCKS: Block[] = [
  {
    title: "cohort",
    body: (
      <p>
        Hard-coded floor of <code>2025-01-01</code>. The spine has been backfilled to Jan 2025, but pre-Mar 2026 rows come from a one-off historical append rather than the live pipeline:
        <code>reporting.demo</code> is organic from <strong>26 Jan 2026 05:09 UTC</strong> (backfill cutoff <strong>25 Jan 23:59 UTC</strong>);
        <code>reporting.lead_cal</code> is organic from <strong>26 Feb 2026 21:36 UTC</strong> (backfill cutoff <strong>26 Feb 21:30 UTC</strong>).
        Lead_cal <code>demo_attended</code> is true for pre-25-Jan-2026 bookings but false for 25 Jan – 26 Feb 2026 (pending re-backfill), so booking → demo linkage in that window is understated.
        Any cohort filter touching pre-Mar 2026 surfaces a banner. Cohort filtering on the dashboard is applied client-side.
      </p>
    ),
  },
  {
    title: "demos",
    body: (
      <p>
        Selects every <code>reporting.demo</code> row tagged as a real demo (<code>follow_up_context.is_demo = 'true'</code>) and organised
        by a <code>@gomarloo.com</code> address. Pulls out the <code>follow_up_context</code> jsonb fields we care about:
        lead status, demo type, urgency, signed-up-on-call flag, advisor count, and the array fields for competitors,
        integrations, security concerns, pain points, pricing signals, value, objections, and transcript signals.
      </p>
    ),
  },
  {
    title: "demo_attendees",
    body: (
      <p>
        Explodes <code>attendee_emails</code> using <code>LATERAL unnest</code> so we get one row per (demo, attendee). Drops empty rows,
        rows missing an <code>@</code>, and internal <code>@gomarloo.com</code> attendees.
      </p>
    ),
  },
  {
    title: "first_demo_per_attendee",
    body: (
      <p>
        Per-attendee earliest demo date. Used to flag <code>is_first_demo_for_attendee</code> on every row, so demo-level rollups can
        deduplicate without losing the multi-attendee signal.
      </p>
    ),
  },
  {
    title: "booking_match",
    body: (
      <p>
        For each demo, finds the closest-in-time <code>reporting.lead_cal</code> booking whose <code>client_email</code> is one of the demo's
        attendees. Window is ±60 days. <code>DISTINCT ON (demo_external_id)</code> picks the nearest match by absolute time gap.
        UTM source / campaign is bucketed into a small set of channels (Meta, Google paid search, LinkedIn, Twitter/X,
        Email signature, In-app demo CTA, Untagged, Other), only buckets the data actually contains.
      </p>
    ),
  },
  {
    title: "attendee_user",
    body: (
      <p>
        Per attendee email, finds the matching <code>public.users</code> row (lifetime, lower-cased). When a user has multiple <code>user_roles</code>,
        prefers status=active, then admin, then adviser, then anything else. This is the join used to determine signup outcome,
        signup timing bucket, role, country, and attribution category.
      </p>
    ),
  },
  {
    title: "paid",
    body: (
      <p>
        Per company, lifetime paid status: <code>on_paid_plan</code> is true if any billing row on that company is <code>plus</code> or <code>pro</code>.
        Earliest qualifying <code>subscription_started_date_utc</code> is kept. <code>billing_plan</code> is the most recent plan.
      </p>
    ),
  },
  {
    title: "activated",
    body: (
      <p>
        A user is activated when they have at least one non-demo, non-deleted <code>meeting_synopsis</code> row. Same definition used by the
        Sign-up Activation dashboard. This is the cross-dashboard bridge metric, so the two pages should always reconcile here.
      </p>
    ),
  },
  {
    title: "Final projection",
    body: (
      <p>
        Joins it all together. <code>signup_timing_bucket</code> is computed from the gap between <code>signup_date_utc</code> and <code>demo_date_utc</code>:
        <code>before</code> (≥1 day prior), <code>on_day</code> (±1 day), <code>within_7d</code>, <code>within_30d</code>, <code>later</code>, or <code>never</code>.
        Output is sorted by demo date desc, then attendee email.
      </p>
    ),
  },
];

const LEAD_CAL_BLOCKS: Block[] = [
  {
    title: "Booking-side mirror",
    body: (
      <p>
        Separate query that's the mirror of the main query. One row per <code>reporting.lead_cal</code> booking, joined
        to the demo window with the same ±60-day attendee-email rule.
      </p>
    ),
  },
  {
    title: "Booking classification",
    body: (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li><strong>matched_real_demo</strong>: paired with an <code>is_demo=true</code> row.</li>
        <li><strong>matched_no_show_only</strong>: paired only with an <code>is_demo=false</code> row (confirmed no-show).</li>
        <li><strong>unmatched</strong>: no demo match in the ±60-day window.</li>
      </ul>
    ),
  },
  {
    title: "Booker outcome",
    body: (
      <p>
        <code>booker_signed_up</code> and <code>booker_company_on_paid_plan</code> are followed forward via the booker email so the
        panel can show no-show recovery candidates and matched-subset booking → demo conversion.
      </p>
    ),
  },
];

function CardGrid({ blocks }: { blocks: Block[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 12,
      }}
    >
      {blocks.map((b) => (
        <div
          key={b.title}
          style={{
            background: "var(--bg-subtle, #FAFAF7)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace", color: "var(--color-teal)", marginBottom: 6 }}>
            {b.title}
          </div>
          <div style={{ fontSize: 12, color: "var(--fg-1)", lineHeight: 1.5 }}>{b.body}</div>
        </div>
      ))}
    </div>
  );
}

export function DemoFunnelExplain() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 13 }}>
        The Demo Conversion dashboard runs two SQL queries: a main funnel query (one row per demo attendee) and a
        supplementary booking-side query (one row per Cal.com booking). Each card below explains one CTE.
      </p>
      <Section title="Main funnel query" blocks={DEMO_BLOCKS} />
      <Section title="Lead_cal supplementary query" blocks={LEAD_CAL_BLOCKS} />
    </div>
  );
}

function Section({ title, blocks }: { title: string; blocks: Block[] }) {
  return (
    <div>
      <h3 style={{ fontSize: 14, margin: "4px 0 10px", borderBottom: "1px solid var(--border-default)", paddingBottom: 6 }}>{title}</h3>
      <CardGrid blocks={blocks} />
    </div>
  );
}

export function DemoFunnelSchema() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ margin: 0, color: "var(--fg-2)" }}>
        Tables and CTEs touched by this dashboard. Click any node to collapse / expand its columns; drag nodes to rearrange.
        Search filters table and column names.
      </p>
      <SchemaDiagram tables={DEMO_FUNNEL_TABLES} joins={DEMO_FUNNEL_JOINS} height={620} />
    </div>
  );
}
